import {gql} from '@apollo/client';

import {MoleculeSchemaInterfaceApiId} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConstants';

export interface TagSchemaWithInterfaceConfig {
  id: string;
  archiveRecord: {purpose: string} | null;
  interfaceConfigs: Array<{interface: {id: string}}>;
  allFieldsJson: Array<{isParentLink?: boolean; requiredLink?: {tagSchema?: {id?: string}} | null}>;
}

export interface TagSchemasWithInterfaceConfigsQueryResult {
  tagSchemas: Array<TagSchemaWithInterfaceConfig>;
}

export const TAG_SCHEMAS_WITH_INTERFACE_CONFIGS_QUERY = gql`
  query TagSchemasWithInterfaceConfigsQuery @skipAudit @engComponent(name: REGISTRY) {
    tagSchemas {
      id
      archiveRecord {
        purpose
      }
      interfaceConfigs {
        interface {
          id
          ... on StaticSchemaInterfaceNode {
            id
          }
        }
      }
      allFieldsJson
    }
  }
`;

/**
 * Given the tag schemas query data, computes the set of Compound schema IDs
 * that are available for linking (i.e., not already referenced by an existing Form schema).
 * Archived Compound schemas are excluded.
 */
export function computeAvailableMoleculeCompoundSchemaIds(
  schemas: Array<TagSchemaWithInterfaceConfig> | undefined
): Set<string> {
  if (!schemas) {
    return new Set();
  }

  const compoundSchemaIds = new Set<string>();
  const formSchemaIds = new Set<string>();

  for (const schema of schemas) {
    const ssiIds = schema.interfaceConfigs.map((cfg) => cfg.interface.id);
    if (ssiIds.includes(MoleculeSchemaInterfaceApiId.MOLECULE_COMPOUND_ID) && schema.archiveRecord == null) {
      compoundSchemaIds.add(schema.id);
    }
    if (ssiIds.includes(MoleculeSchemaInterfaceApiId.MOLECULE_FORM_ID) && schema.archiveRecord == null) {
      formSchemaIds.add(schema.id);
    }
  }

  const takenCompoundSchemaIds = new Set<string>();
  for (const schema of schemas) {
    if (!formSchemaIds.has(schema.id)) {
      continue;
    }
    for (const field of schema.allFieldsJson ?? []) {
      if (field.isParentLink && field.requiredLink?.tagSchema?.id) {
        takenCompoundSchemaIds.add(field.requiredLink.tagSchema.id);
      }
    }
  }

  const availableIds = new Set<string>();
  for (const id of compoundSchemaIds) {
    if (!takenCompoundSchemaIds.has(id)) {
      availableIds.add(id);
    }
  }

  return availableIds;
}
